import { useEffect, useState } from 'react';
import { Link as RouterLink } from '@tanstack/react-router';
import type { Label, VideoStatus } from '@ddp/shared';
import Button from '@carbon/react/es/components/Button';
import TextInput from '@carbon/react/es/components/TextInput/TextInput';
import Select from '@carbon/react/es/components/Select/Select';
import SelectItem from '@carbon/react/es/components/SelectItem/SelectItem';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';
import Search from '@carbon/icons-react/es/Search';

import { apiFetch } from '../api/client.js';
import { VideoThumbnail } from '../features/video/VideoThumbnail.js';
import { useCaptureAssets } from '../features/hardware/use-capture-assets.js';
import { GeoBoundingBoxPicker } from '../features/video/GeoBoundingBoxPicker.js';
import { useVideoSearch, type SearchFilters } from '../features/video/use-video-search.js';
import { useT, type MessageKey } from '../i18n/index.js';
import { PageHeader } from '../components/PageHeader.js';

/** Statuts proposés au filtre, avec leur clé de libellé (clés littérales : `t` n'accepte pas de clé calculée). */
const STATUS_LABEL_KEY: Record<VideoStatus, MessageKey> = {
  READY: 'data.status.ready',
  PROCESSING: 'data.status.processing',
  UPLOADED: 'data.status.uploaded',
  UPLOADING: 'data.status.uploading',
  PENDING: 'data.status.pending',
  FAILED: 'data.status.failed',
};
const STATUSES = Object.keys(STATUS_LABEL_KEY) as VideoStatus[];

const INITIAL: SearchFilters = { sortBy: 'createdAt', sortOrder: 'desc' };

/**
 * Recherche avancée (`/data/videos/search`, FR-19→22) : une barre plein texte et un panneau de
 * filtres (statut, appareil, pilote, dates, annotations, labels) consommant `POST /videos/search`,
 * avec pagination par curseur (« Charger plus »). L'emprise géographique sur carte est un incrément
 * ultérieur ; le reste des critères du contrat est couvert ici.
 */
export function VideoSearchPage() {
  const t = useT();
  const captureAssets = useCaptureAssets();
  const { items, nextCursor, loading, error, searched, search, loadMore } = useVideoSearch();

  const [filters, setFilters] = useState<SearchFilters>(INITIAL);
  const [labels, setLabels] = useState<Label[]>([]);

  useEffect(() => {
    apiFetch<Label[]>('/labels')
      .then(setLabels)
      .catch(() => setLabels([]));
  }, []);

  // Première recherche au chargement : les vidéos les plus récentes, sans filtre.
  useEffect(() => {
    search(INITIAL);
    // On ne relance qu'au montage ; les recherches suivantes passent par le bouton.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = <K extends keyof SearchFilters>(key: K, value: SearchFilters[K]) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const toggleLabel = (id: string) =>
    setFilters((prev) => {
      const current = prev.labelIds ?? [];
      const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
      return { ...prev, labelIds: next.length > 0 ? next : undefined };
    });

  const onReset = () => {
    setFilters(INITIAL);
    search(INITIAL);
  };

  // Sélecteur d'annotations en tri-état : '' (toutes), 'yes', 'no'.
  const annotationsValue =
    filters.hasAnnotations === undefined ? '' : filters.hasAnnotations ? 'yes' : 'no';

  return (
    <section className="ddp-page__section ddp-videos ddp-search">
      <RouterLink to="/data/videos" className="ddp-page-back">
        {t('search.back')}
      </RouterLink>
      <PageHeader title={t('search.title')} lead={t('search.subtitle')} />

      <form
        className="ddp-search__form"
        onSubmit={(event) => {
          event.preventDefault();
          search(filters);
        }}
      >
        <div className="ddp-search__query">
          <TextInput
            id="search-q"
            labelText={t('search.query')}
            placeholder={t('search.queryPlaceholder')}
            value={filters.q ?? ''}
            onChange={(event) => set('q', event.target.value)}
          />
        </div>

        <div className="ddp-search__filters">
          <Select
            id="search-status"
            labelText={t('search.status')}
            value={filters.status ?? ''}
            onChange={(event) => set('status', (event.target.value || undefined) as VideoStatus)}
          >
            <SelectItem value="" text={t('search.any')} />
            {STATUSES.map((status) => (
              <SelectItem key={status} value={status} text={t(STATUS_LABEL_KEY[status])} />
            ))}
          </Select>

          {captureAssets.length > 0 && (
            <Select
              id="search-asset"
              labelText={t('search.asset')}
              value={filters.assetId ?? ''}
              onChange={(event) => set('assetId', event.target.value || undefined)}
            >
              <SelectItem value="" text={t('search.anyAsset')} />
              {captureAssets.map((asset) => (
                <SelectItem key={asset.id} value={asset.id} text={asset.name} />
              ))}
            </Select>
          )}

          <TextInput
            id="search-pilot"
            labelText={t('search.pilot')}
            value={filters.pilotName ?? ''}
            onChange={(event) => set('pilotName', event.target.value || undefined)}
          />

          <TextInput
            id="search-from"
            type="date"
            labelText={t('search.flownFrom')}
            value={filters.flownFrom ?? ''}
            onChange={(event) => set('flownFrom', event.target.value || undefined)}
          />
          <TextInput
            id="search-to"
            type="date"
            labelText={t('search.flownTo')}
            value={filters.flownTo ?? ''}
            onChange={(event) => set('flownTo', event.target.value || undefined)}
          />

          <Select
            id="search-annotations"
            labelText={t('search.annotations')}
            value={annotationsValue}
            onChange={(event) => {
              const v = event.target.value;
              set('hasAnnotations', v === '' ? undefined : v === 'yes');
            }}
          >
            <SelectItem value="" text={t('search.any')} />
            <SelectItem value="yes" text={t('search.withAnnotations')} />
            <SelectItem value="no" text={t('search.withoutAnnotations')} />
          </Select>

          <Select
            id="search-sort"
            labelText={t('search.sortBy')}
            value={filters.sortBy}
            onChange={(event) => set('sortBy', event.target.value as SearchFilters['sortBy'])}
          >
            <SelectItem value="createdAt" text={t('search.sort.createdAt')} />
            <SelectItem value="flownAt" text={t('search.sort.flownAt')} />
            <SelectItem value="title" text={t('search.sort.title')} />
            <SelectItem value="duration" text={t('search.sort.duration')} />
          </Select>
          <Select
            id="search-order"
            labelText={t('search.order')}
            value={filters.sortOrder}
            onChange={(event) => set('sortOrder', event.target.value as SearchFilters['sortOrder'])}
          >
            <SelectItem value="desc" text={t('search.order.desc')} />
            <SelectItem value="asc" text={t('search.order.asc')} />
          </Select>
        </div>

        {labels.length > 0 && (
          <fieldset className="ddp-search__labels">
            <legend>{t('search.labels')}</legend>
            {labels.map((label) => {
              const active = (filters.labelIds ?? []).includes(label.id);
              return (
                <button
                  key={label.id}
                  type="button"
                  className={`ddp-search__chip${active ? ' is-active' : ''}`}
                  aria-pressed={active}
                  onClick={() => toggleLabel(label.id)}
                >
                  <span className="ddp-search__chip-dot" style={{ backgroundColor: label.color }} />
                  {label.name}
                </button>
              );
            })}
          </fieldset>
        )}

        <fieldset className="ddp-search__geo">
          <legend>{t('search.geo.title')}</legend>
          <GeoBoundingBoxPicker
            value={filters.bbox ?? null}
            onChange={(bbox) => set('bbox', bbox ?? undefined)}
          />
        </fieldset>

        <div className="ddp-search__actions">
          <Button type="submit" renderIcon={Search} disabled={loading}>
            {t('search.submit')}
          </Button>
          <Button kind="ghost" type="button" onClick={onReset} disabled={loading}>
            {t('search.reset')}
          </Button>
        </div>
      </form>

      {error && (
        <InlineNotification
          kind="error"
          title={t('search.errorTitle')}
          subtitle={error}
          lowContrast
          hideCloseButton
        />
      )}

      {searched && !loading && items.length === 0 && !error && (
        <p className="ddp-videos__empty">{t('search.noResults')}</p>
      )}

      {items.length > 0 && (
        <>
          <p className="ddp-search__count">
            {items.length} {t('search.results')}
          </p>
          <ul className="ddp-videos__grid">
            {items.map((video) => (
              <li key={video.id} className="ddp-videos__cell">
                <RouterLink
                  to="/videos/$videoId"
                  params={{ videoId: video.id }}
                  className="ddp-videos__card"
                >
                  <VideoThumbnail
                    videoId={video.id}
                    status={video.status}
                    coverUrl={video.coverUrl}
                    className="ddp-videos__thumb"
                    mediaClassName="ddp-videos__thumb-media"
                    emptyClassName="ddp-videos__thumb-empty"
                  />
                  <div className="ddp-videos__card-top">
                    <span className="ddp-videos__title" title={video.title}>
                      {video.title}
                    </span>
                  </div>
                  {video.assetName && <p className="ddp-search__card-asset">{video.assetName}</p>}
                </RouterLink>
              </li>
            ))}
          </ul>
        </>
      )}

      {nextCursor && (
        <div className="ddp-search__more">
          <Button kind="tertiary" onClick={loadMore} disabled={loading}>
            {loading ? t('search.loading') : t('search.loadMore')}
          </Button>
        </div>
      )}
    </section>
  );
}
